import { useCallback, useEffect, useRef, useState } from 'react';
import { firebase } from '../../firebase/firebase.singleton';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../lib/logging';
import {
  decodeJWTExp,
  getIntercomJWT,
} from '../../services/Intercom/IntercomJWT.service';
import { tIntercomJWTError } from '../../services/Intercom/IntercomJWT.types';

export type tUseIntercomJWTReturn = {
  intercomJWT: string | null;
  isLoading: boolean;
  error: tIntercomJWTError | null;
  refreshJWT: () => Promise<void>;
};

const REFRESH_INTERVAL_MS = 8 * 60 * 1000; // 8 minutes in milliseconds
const REFRESH_BUFFER_SECONDS = 120; // Refresh 2 minutes before expiration

/**
 * Normalizes an error to tIntercomJWTError format
 * Checks if error already has a known code, otherwise creates UNKNOWN error
 */
function normalizeError(err: unknown): tIntercomJWTError {
  // Check if error already has a known tIntercomJWTError code
  if (
    err &&
    typeof err === 'object' &&
    'code' in err &&
    typeof err.code === 'string' &&
    ['UNAUTHORIZED', 'FORBIDDEN', 'NETWORK_ERROR', 'UNKNOWN'].includes(err.code)
  ) {
    // Error already has a valid code, use it
    return {
      code: err.code as tIntercomJWTError['code'],
      message:
        'message' in err && typeof err.message === 'string'
          ? err.message
          : String(err),
      originalError: err,
    };
  }

  // Error doesn't have a known code, create UNKNOWN error
  const errorMessage =
    err instanceof Error
      ? err.message
      : err && typeof err === 'object' && 'message' in err
        ? String(err.message)
        : String(err ?? 'Unknown error');

  return {
    code: 'UNKNOWN',
    message: errorMessage,
    originalError: err,
  };
}

/**
 * Hook to manage Intercom JWT lifecycle
 * - Fetches JWT from backend when user is authenticated
 * - Automatically refreshes JWT before expiration
 * - Cleans up timers on unmount
 */
export function useIntercomJWT(): tUseIntercomJWTReturn {
  const [intercomJWT, setIntercomJWT] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<tIntercomJWTError | null>(null);

  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef(false);

  /**
   * Fetches fresh Intercom JWT from backend
   */
  const fetchJWT = useCallback(async (): Promise<void> => {
    const currentUser = firebase.auth.currentUser;

    if (!currentUser) {
      setIntercomJWT(null);
      setError(null);
      return;
    }

    const uid = currentUser.uid;

    if (!uid) {
      LLog.warn(
        {
          component: LOG_COMPONENTS.AUTH,
          method: 'useIntercomJWT',
          operation: LOG_OPERATIONS.AUTH,
        },
        'No Firebase UID available',
      );
      return;
    }

    // Prevent concurrent refresh attempts
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      // Get fresh ID token (force refresh with true)
      const idToken = await currentUser.getIdToken(true);

      // Fetch Intercom JWT from backend
      const jwt = await getIntercomJWT(uid, idToken);

      setIntercomJWT(jwt);
      setError(null);

      LLog.info(
        {
          component: LOG_COMPONENTS.AUTH,
          method: 'useIntercomJWT',
          operation: LOG_OPERATIONS.AUTH,
        },
        'Successfully fetched Intercom JWT',
      );
    } catch (err) {
      // Normalize error before using it
      const jwtError = normalizeError(err);
      setError(jwtError);
      setIntercomJWT(null);

      // If unauthorized, try to refresh ID token and retry once
      if (jwtError.code === 'UNAUTHORIZED' && currentUser) {
        try {
          LLog.info(
            {
              component: LOG_COMPONENTS.AUTH,
              method: 'useIntercomJWT',
              operation: LOG_OPERATIONS.AUTH,
            },
            'Retrying with refreshed ID token',
          );

          const refreshedIdToken = await currentUser.getIdToken(true);
          const retryJWT = await getIntercomJWT(uid, refreshedIdToken);

          setIntercomJWT(retryJWT);
          setError(null);
        } catch (retryErr) {
          // Normalize retry error as well
          const normalizedRetryError = normalizeError(retryErr);
          setError(normalizedRetryError);

          LLog.error(
            {
              component: LOG_COMPONENTS.AUTH,
              method: 'useIntercomJWT',
              operation: LOG_OPERATIONS.ERROR,
            },
            'Retry after ID token refresh failed',
            retryErr instanceof Error ? retryErr : new Error(String(retryErr)),
          );
        }
      }
    } finally {
      setIsLoading(false);
      isRefreshingRef.current = false;
    }
  }, []);

  /**
   * Schedules automatic JWT refresh
   */
  const scheduleRefresh = useCallback(
    (jwt: string) => {
      // Clear existing timer
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      // Try to decode JWT to get expiration time
      const exp = decodeJWTExp(jwt);

      let refreshDelay: number;

      if (exp) {
        // Calculate delay to refresh 2 minutes before expiration
        const now = Math.floor(Date.now() / 1000);
        const timeUntilExp = exp - now;
        const refreshTime = timeUntilExp - REFRESH_BUFFER_SECONDS;

        if (refreshTime > 0) {
          refreshDelay = refreshTime * 1000; // Convert to milliseconds
        } else {
          // JWT is expiring soon or already expired, refresh immediately
          refreshDelay = 0;
        }
      } else {
        // Fallback to 8 minutes if we can't decode exp
        refreshDelay = REFRESH_INTERVAL_MS;
      }

      // Ensure minimum delay of 1 minute only when refreshDelay is positive
      // Allow immediate refresh (0 or negative) when token is expired
      if (refreshDelay > 0) {
        refreshDelay = Math.max(refreshDelay, 60 * 1000);
      }

      LLog.info(
        {
          component: LOG_COMPONENTS.AUTH,
          method: 'useIntercomJWT',
          operation: LOG_OPERATIONS.AUTH,
        },
        `Scheduling JWT refresh in ${Math.round(refreshDelay / 1000 / 60)} minutes`,
      );

      refreshTimerRef.current = setTimeout(() => {
        fetchJWT();
      }, refreshDelay);
    },
    [fetchJWT],
  );

  /**
   * Public method to manually refresh JWT
   */
  const refreshJWT = useCallback(async (): Promise<void> => {
    await fetchJWT();
  }, [fetchJWT]);

  // Effect to fetch JWT when user is authenticated
  useEffect(() => {
    // Listen to auth state changes instead of checking once
    const unsubscribe = firebase.auth.onAuthStateChanged((user) => {
      if (user?.uid) {
        fetchJWT();
      } else {
        // User not authenticated, clear JWT
        setIntercomJWT(null);
        setError(null);

        // Clear any pending refresh timers
        if (refreshTimerRef.current) {
          clearTimeout(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      }
    });

    return () => {
      unsubscribe();
    };
  }, [fetchJWT]);

  // Effect to schedule refresh when JWT is obtained
  useEffect(() => {
    if (intercomJWT && !error) {
      scheduleRefresh(intercomJWT);
    }
  }, [intercomJWT, error, scheduleRefresh]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, []);

  return {
    intercomJWT,
    isLoading,
    error,
    refreshJWT,
  };
}
